import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { useNav } from "../context/NavContext";

const DEFAULT_OTP = "454545";

const T = {
  bg:          "#0d1b2e",
  inputBg:     "rgba(255,255,255,0.07)",
  inputBorder: "rgba(255,255,255,0.13)",
  cyan:        "#0ea5e9",
  cyanDim:     "rgba(14,165,233,0.18)",
  cyanBorder:  "rgba(14,165,233,0.35)",
  textPrimary: "#f0f9ff",
  textSub:     "#7dd3fc",
  textMuted:   "rgba(255,255,255,0.40)",
  error:       "#f87171",
  success:     "#86efac",
};

// ── Spinner ──────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
      style={{ animation: "spin 0.8s linear infinite", flexShrink: 0 }}>
      <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.25)" strokeWidth="2.5" />
      <path d="M8 2a6 6 0 0 1 6 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

// ── OTP boxes ────────────────────────────────────────────────────────────────

function OtpInput({ value, onChange }) {
  const refs = useRef([]);

  const handleChange = (i, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...value];
    next[i] = val;
    onChange(next);
    if (val && i < 5) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if ((e.key === "Backspace" || e.key === "Delete") && !value[i] && i > 0) {
      refs.current[i - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length) {
      const next = [...value];
      pasted.split("").forEach((ch, i) => { next[i] = ch; });
      onChange(next);
      refs.current[Math.min(pasted.length, 5)]?.focus();
    }
  };

  return (
    <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 8 }}>
      {Array.from({ length: 6 }).map((_, i) => (
        <input
          key={i}
          id={`otp-${i}`}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          style={{
            width: 44,
            height: 52,
            textAlign: "center",
            fontSize: 22,
            fontWeight: 700,
            borderRadius: 12,
            border: `1.5px solid ${value[i] ? T.cyan : T.inputBorder}`,
            background: T.inputBg,
            color: T.textPrimary,
            outline: "none",
            caretColor: T.cyan,
            transition: "border-color 0.15s",
          }}
        />
      ))}
    </div>
  );
}

// ── Main Sheet ────────────────────────────────────────────────────────────────

export default function PhoneSheet({ show, onClose }) {
  const { login } = useAuth();
  const { navigate } = useNav();

  const [step, setStep]         = useState("phone"); // "phone" | "otp"
  const [phone, setPhone]       = useState("");
  const [otp, setOtp]           = useState(["", "", "", "", "", ""]);
  const [agreed, setAgreed]     = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);
  const [timer, setTimer]       = useState(60);
  const [canResend, setCanResend] = useState(false);

  // Countdown
  useEffect(() => {
    if (step !== "otp") return;
    if (timer === 0) { setCanResend(true); return; }
    const t = setTimeout(() => setTimer((v) => v - 1), 1000);
    return () => clearTimeout(t);
  }, [timer, step]);

  // Focus first OTP box
  useEffect(() => {
    if (step === "otp") setTimeout(() => document.getElementById("otp-0")?.focus(), 100);
  }, [step]);

  // Reset when closed
  useEffect(() => {
    if (!show) {
      setStep("phone"); setPhone(""); setOtp(["","","","","",""]); 
      setAgreed(false); setError(""); setTimer(60); setCanResend(false);
    }
  }, [show]);

  const handlePhoneChange = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(0, 10);
    setPhone(v);
    if (error) setError("");
  };

  const handleSendOtp = () => {
    if (phone.length !== 10) { setError("Enter a valid 10-digit mobile number"); return; }
    if (!agreed) { setError("Please accept the terms to continue"); return; }
    setError("");
    setLoading(true);
    // Simulate API
    setTimeout(() => {
      setLoading(false);
      setStep("otp");
      setTimer(60);
      setCanResend(false);
    }, 800);
  };

  const handleVerifyOtp = () => {
    const entered = otp.join("");
    if (entered.length !== 6) { setError("Enter the 6-digit OTP"); return; }
    if (entered !== DEFAULT_OTP) { setError("Incorrect OTP."); return; }
    setError("");
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      login(phone);
      onClose();
      navigate("loanDetails");
    }, 800);
  };

  const handleResend = () => {
    setOtp(["","","","","",""]); setTimer(60); setCanResend(false); setError("");
  };

  const btnDisabled = step === "phone"
    ? phone.length !== 10 || !agreed || loading
    : otp.join("").length !== 6 || loading;

  if (!show) return null;

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 98,
      }} />

      {/* Sheet */}
      <div style={{
        position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)",
        width: "100%", maxWidth: 480, zIndex: 99,
        borderRadius: "24px 24px 0 0",
        background: T.bg,
        padding: "0 0 40px",
        boxShadow: "0 -20px 60px rgba(0,0,0,0.5)",
        animation: "slideUp 0.3s ease",
      }}>
        {/* Handle */}
        <div style={{ display: "flex", justifyContent: "center", padding: "16px 0 4px" }}>
          <div style={{ width: 40, height: 4, borderRadius: 2, background: "rgba(255,255,255,0.15)" }} />
        </div>

        <div style={{ padding: "12px 24px 0" }}>
          {/* Step dots */}
          <div style={{ display: "flex", gap: 6, justifyContent: "center", marginBottom: 20 }}>
            {["phone", "otp"].map((s) => (
              <div key={s} style={{
                width: s === step ? 24 : 8, height: 8, borderRadius: 4,
                background: s === step ? T.cyan : "rgba(255,255,255,0.13)",
                transition: "all 0.3s",
              }} />
            ))}
          </div>

          {/* Icon */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <div style={{
              width: 56, height: 56, borderRadius: "50%",
              background: T.cyanDim, border: `1px solid ${T.cyanBorder}`,
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26,
            }}>
              {step === "phone" ? "📱" : "🔐"}
            </div>
          </div>

          {/* Title */}
          <h2 style={{ fontSize: 20, fontWeight: 700, color: T.textPrimary, textAlign: "center", marginBottom: 6 }}>
            {step === "phone" ? "Mobile Verification" : "Enter 6-Digit OTP"}
          </h2>
          <p style={{ fontSize: 13, color: T.textSub, textAlign: "center", marginBottom: 24, lineHeight: 1.5 }}>
            {step === "phone"
              ? "Enter your mobile number to get started"
              : `OTP sent to +91 ${phone}   (hint: 454545)`}
          </p>

          {/* ── PHONE STEP ── */}
          {step === "phone" && (
            <>
              <div style={{
                display: "flex", alignItems: "center",
                background: T.inputBg,
                border: `1px solid ${phone.length === 10 ? T.cyan : T.inputBorder}`,
                borderRadius: 14, overflow: "hidden", marginBottom: 6,
              }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "14px 12px", borderRight: `1px solid ${T.inputBorder}`,
                }}>
                  <span style={{ fontSize: 18 }}>🇮🇳</span>
                  <span style={{ fontSize: 14, color: T.textSub, fontWeight: 600 }}>+91</span>
                </div>
                <input
                  type="tel"
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="Enter mobile number"
                  style={{
                    flex: 1, padding: "14px 12px",
                    background: "transparent", border: "none", outline: "none",
                    fontSize: 16, color: T.textPrimary, caretColor: T.cyan,
                  }}
                />
                {phone.length === 10 && (
                  <div style={{ paddingRight: 14 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: "50%",
                      background: T.success, boxShadow: `0 0 8px ${T.success}`,
                    }} />
                  </div>
                )}
              </div>

              {/* T&C */}
              <label style={{ display: "flex", alignItems: "flex-start", gap: 10, margin: "14px 0 20px", cursor: "pointer" }}>
                <div onClick={() => { setAgreed(!agreed); setError(""); }} style={{
                  width: 20, height: 20, borderRadius: 6, flexShrink: 0, marginTop: 1,
                  border: `1.5px solid ${agreed ? T.cyan : T.inputBorder}`,
                  background: agreed ? T.cyanDim : "transparent",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", transition: "all 0.2s",
                }}>
                  {agreed && (
                    <svg width="12" height="10" viewBox="0 0 12 10" fill="none">
                      <path d="M1 5L4.5 8.5L11 1.5" stroke="#0ea5e9" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <p style={{ fontSize: 12, color: T.textMuted, lineHeight: 1.6 }}>
                  I agree to the{" "}
                  <span style={{ color: T.cyan, textDecoration: "underline", cursor: "pointer" }}>
                    Terms & Conditions
                  </span>{" "}
                  and{" "}
                  <span style={{ color: T.cyan, textDecoration: "underline", cursor: "pointer" }}>
                    Privacy Policy
                  </span>
                </p>
              </label>
            </>
          )}

          {/* ── OTP STEP ── */}
          {step === "otp" && (
            <>
              <OtpInput value={otp} onChange={setOtp} />

              {/* Resend row */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, marginTop: 6 }}>
                <span style={{ fontSize: 13, color: T.textMuted }}>Didn't receive OTP?</span>
                {!canResend ? (
                  <span style={{ fontSize: 13, color: T.textSub }}>
                    Resend in <strong style={{ color: T.cyan }}>{timer}s</strong>
                  </span>
                ) : (
                  <button onClick={handleResend} style={{
                    background: "transparent", border: `1px solid ${T.cyanBorder}`,
                    borderRadius: 8, padding: "6px 14px", color: T.cyan,
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}>
                    Resend OTP
                  </button>
                )}
              </div>

              {/* Security badge */}
              <div style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "10px 14px",
                background: T.cyanDim, border: `1px solid ${T.cyanBorder}`,
                borderRadius: 10, marginBottom: 16,
              }}>
                <span style={{ fontSize: 16 }}>🔒</span>
                <span style={{ fontSize: 12, color: T.textSub }}>Your data is encrypted and 100% secure</span>
              </div>
            </>
          )}

          {/* Error */}
          {error && (
            <p style={{ fontSize: 12, color: T.error, textAlign: "center", marginBottom: 10 }}>
              {error}
            </p>
          )}

          {/* Primary button */}
          <button
            onClick={step === "phone" ? handleSendOtp : handleVerifyOtp}
            disabled={btnDisabled}
            style={{
              width: "100%", padding: "16px", border: "none", borderRadius: 14,
              background: btnDisabled
                ? "rgba(14,165,233,0.2)"
                : "linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)",
              color: btnDisabled ? "rgba(255,255,255,0.35)" : "#fff",
              fontSize: 15, fontWeight: 600,
              cursor: btnDisabled ? "not-allowed" : "pointer",
              boxShadow: btnDisabled ? "none" : "0 4px 20px rgba(14,165,233,0.35)",
              transition: "all 0.2s",
              display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              marginBottom: step === "otp" ? 12 : 0,
            }}
          >
            {loading ? <><Spinner /> {step === "phone" ? "Sending…" : "Verifying…"}</> :
              step === "phone" ? "Send OTP →" : "Verify & Continue →"}
          </button>

          {/* Back link in OTP step */}
          {step === "otp" && (
            <button onClick={() => { setStep("phone"); setError(""); setOtp(["","","","","",""]); }} style={{
              width: "100%", padding: "10px", background: "transparent", border: "none",
              color: T.textMuted, fontSize: 13, cursor: "pointer", textAlign: "center",
            }}>
              ← Change mobile number
            </button>
          )}
        </div>
      </div>
    </>
  );
}
